// @ts-nocheck -- staged migration: add explicit types before removing this directive.
// Original grammar explanations and example sentences written for KanaLearn.
// Grammar point names, patterns, and JLPT groupings are standard descriptive
// facts about the language (not anyone's copyrightable expression) - the
// wording of every explanation and every example sentence below is our own.
export const grammarPoints = [
    {
        id: "n5-wa-topic",
        pattern: "Xは Yです",
        jlptLevel: 5,
        title: "は - the topic marker",
        meaning: "Marks X as the topic of the sentence; Y describes or identifies it.",
        explanation:
            "は (pronounced \"wa\" when used as a particle) marks whatever comes before it as the sentence's " +
            "topic - what the rest of the sentence is about. It doesn't mean X performs the action or is the " +
            "grammatical subject the way English \"subject\" works; it just sets up what's being discussed, and " +
            "です (the polite copula, \"is/am/are\") often follows to say something about it.",
        formation: "Noun + は + Noun/Adjective + です",
        examples: [
            { japanese: "わたしは学生です。", reading: "わたしはがくせいです。", english: "I am a student." },
            { japanese: "これは本です。", reading: "これはほんです。", english: "This is a book." },
        ],
    },
    {
        id: "n5-mo-also",
        pattern: "Xも Yです",
        jlptLevel: 5,
        title: "も - also, too",
        meaning: "Replaces は when X shares the same property as something already mentioned.",
        explanation:
            "も means \"also\" or \"too.\" It takes the place of は (or を/が) on the item that shares the same " +
            "quality just described about something else, so you don't have to repeat the whole sentence.",
        formation: "Noun + も + ...",
        examples: [
            {
                japanese: "田中さんは先生です。鈴木さんも先生です。",
                reading: "たなかさんはせんせいです。すずきさんもせんせいです。",
                english: "Mr. Tanaka is a teacher. Ms. Suzuki is also a teacher.",
            },
            {
                japanese: "わたしはコーヒーが好きです。紅茶も好きです。",
                reading: "わたしはコーヒーがすきです。こうちゃもすきです。",
                english: "I like coffee. I also like tea.",
            },
        ],
    },
    {
        id: "n5-no-possessive",
        pattern: "Xの Y",
        jlptLevel: 5,
        title: "の - possessive and associative",
        meaning: "Links two nouns, usually showing X owns, describes, or is associated with Y.",
        explanation:
            "の joins two nouns together, with the second noun being modified by the first. It most often shows " +
            "possession (\"my book\"), but also links nouns in looser associative relationships, like a place " +
            "with what happens there or a category with its members.",
        formation: "Noun1 + の + Noun2",
        examples: [
            { japanese: "これはわたしの本です。", reading: "これはわたしのほんです。", english: "This is my book." },
            { japanese: "日本語の先生です。", reading: "にほんごのせんせいです。", english: "I'm a Japanese-language teacher." },
        ],
    },
    {
        id: "n5-wo-object",
        pattern: "Xを + verb",
        jlptLevel: 5,
        title: "を - the direct object marker",
        meaning: "Marks X as the thing the verb directly acts on.",
        explanation:
            "を (pronounced \"o\") marks the direct object of an action verb - the thing being eaten, read, " +
            "bought, and so on. It almost never appears anywhere except right before the verb it belongs to.",
        formation: "Noun + を + Verb",
        examples: [
            { japanese: "パンを食べます。", reading: "パンをたべます。", english: "I eat bread." },
            { japanese: "新聞を読みます。", reading: "しんぶんをよみます。", english: "I read the newspaper." },
        ],
    },
    {
        id: "n5-ni-time-location",
        pattern: "Xに",
        jlptLevel: 5,
        title: "に - a point in time, or existence at a place",
        meaning: "Marks a specific time something happens, or the place something/someone exists.",
        explanation:
            "に has several jobs, but two of the most common at this level are marking a specific point in time " +
            "(\"at 7 o'clock,\" \"on Monday\") and marking the location where something or someone simply exists, " +
            "used together with あります/います (see that entry below). Don't confuse it with で, which marks " +
            "where an action takes place rather than where something sits.",
        formation: "Time/Place + に",
        examples: [
            { japanese: "七時に起きます。", reading: "しちじにおきます。", english: "I get up at seven o'clock." },
            { japanese: "猫は部屋にいます。", reading: "ねこはへやにいます。", english: "The cat is in the room." },
        ],
    },
    {
        id: "n5-de-location-action",
        pattern: "Xで + verb",
        jlptLevel: 5,
        title: "で - where an action happens, or the means used",
        meaning: "Marks the place an action takes place, or the tool/method used to do it.",
        explanation:
            "で marks the location where an action happens - contrast this with に, which marks where something " +
            "merely exists. で also marks the means or instrument used to do something, like the tool you write " +
            "with or the language you speak in.",
        formation: "Place/Tool + で + Verb",
        examples: [
            { japanese: "図書館で勉強します。", reading: "としょかんでべんきょうします。", english: "I study at the library." },
            { japanese: "はしで食べます。", reading: "はしでたべます。", english: "I eat with chopsticks." },
        ],
    },
    {
        id: "n5-e-direction",
        pattern: "Xへ + verb of movement",
        jlptLevel: 5,
        title: "へ - direction, toward a place",
        meaning: "Marks the direction or destination of a movement verb, similar to に.",
        explanation:
            "へ (pronounced \"e\" when used as a particle) marks the direction something moves toward. It's " +
            "interchangeable with に in most everyday sentences describing movement, though へ puts a little " +
            "more emphasis on the journey or direction itself rather than the exact arrival point.",
        formation: "Place + へ + Verb of movement",
        examples: [
            { japanese: "学校へ行きます。", reading: "がっこうへいきます。", english: "I go to school." },
            { japanese: "来週、大阪へ行きます。", reading: "らいしゅう、おおさかへいきます。", english: "Next week I'm going to Osaka." },
        ],
    },
    {
        id: "n5-to-and-with",
        pattern: "AとB",
        jlptLevel: 5,
        title: "と - and, with",
        meaning: "Connects a complete, closed list of nouns (\"and\"), or marks who you do something with (\"with\").",
        explanation:
            "Between two nouns, と means \"and,\" but unlike や it lists every item in the group - nothing is " +
            "left out. と can also follow a person to mean \"with,\" showing who you're doing an action together with.",
        formation: "Noun + と + Noun / Person + と + Verb",
        examples: [
            { japanese: "りんごとみかんを買いました。", reading: "りんごとみかんをかいました。", english: "I bought apples and mandarins." },
            { japanese: "友達と映画を見ました。", reading: "ともだちとえいがをみました。", english: "I watched a movie with a friend." },
        ],
    },
    {
        id: "n5-ya-and-among-others",
        pattern: "AやB",
        jlptLevel: 5,
        title: "や - and, among other things",
        meaning: "Connects a partial, open-ended list of nouns - implies there's more not mentioned.",
        explanation:
            "や also means \"and,\" but marks a partial list - a few representative examples out of a larger set, " +
            "rather than every item. It's often paired with など (\"etc.\") at the end for extra clarity, though " +
            "など isn't required.",
        formation: "Noun + や + Noun (+ など)",
        examples: [
            {
                japanese: "机の上に本やノートがあります。",
                reading: "つくえのうえにほんやノートがあります。",
                english: "There are things like books and notebooks (among others) on the desk.",
            },
            {
                japanese: "週末は映画や音楽を楽しみます。",
                reading: "しゅうまつはえいがやおんがくをたのしみます。",
                english: "On weekends I enjoy things like movies and music.",
            },
        ],
    },
    {
        id: "n5-ka-question",
        pattern: "〜か。",
        jlptLevel: 5,
        title: "か - turning a statement into a question",
        meaning: "Added to the end of a sentence to turn it into a yes/no or open question.",
        explanation:
            "Adding か to the end of a plain statement turns it into a question, without needing to change word " +
            "order the way English does. In polite speech, か often takes the place of a rising question " +
            "intonation, though a question mark is still commonly written for clarity.",
        formation: "Sentence (polite form) + か",
        examples: [
            { japanese: "田中さんは学生ですか。", reading: "たなかさんはがくせいですか。", english: "Is Mr. Tanaka a student?" },
            { japanese: "何を食べますか。", reading: "なにをたべますか。", english: "What will you eat?" },
        ],
    },
    {
        id: "n5-masu-polite-present",
        pattern: "〜ます",
        jlptLevel: 5,
        title: "ます - the polite present/future verb ending",
        meaning: "The standard polite ending for a verb describing a habitual action or something that will happen.",
        explanation:
            "Attaching ます to a verb's stem produces the neutral-polite form used in everyday conversation with " +
            "people you're not close to. It covers both the present habitual (\"I do X\") and the future (\"I " +
            "will do X\") - Japanese doesn't distinguish these the way English does.",
        formation: "Verb stem + ます",
        examples: [
            { japanese: "毎日日本語を勉強します。", reading: "まいにちにほんごをべんきょうします。", english: "I study Japanese every day." },
            { japanese: "明日、友達に会います。", reading: "あした、ともだちにあいます。", english: "I'll meet a friend tomorrow." },
        ],
    },
    {
        id: "n5-masen-polite-negative",
        pattern: "〜ません",
        jlptLevel: 5,
        title: "ません - the polite negative",
        meaning: "The negative counterpart to ます: \"does not / will not.\"",
        explanation:
            "Swapping ます for ません negates the verb in the same polite register. It's used for anything that " +
            "doesn't happen, won't happen, or isn't done.",
        formation: "Verb stem + ません",
        examples: [
            { japanese: "肉を食べません。", reading: "にくをたべません。", english: "I don't eat meat." },
            { japanese: "今日は学校へ行きません。", reading: "きょうはがっこうへいきません。", english: "I'm not going to school today." },
        ],
    },
    {
        id: "n5-mashita-polite-past",
        pattern: "〜ました / 〜ませんでした",
        jlptLevel: 5,
        title: "ました / ませんでした - the polite past",
        meaning: "The past-tense forms of ます (did) and ません (did not).",
        explanation:
            "ました is the plain past of ます (\"did X\"), and ませんでした is the past of ません (\"did not do " +
            "X\"). Together they cover every polite past-tense statement a beginner needs.",
        formation: "Verb stem + ました / ませんでした",
        examples: [
            { japanese: "昨日、映画を見ました。", reading: "きのう、えいがをみました。", english: "I watched a movie yesterday." },
            { japanese: "朝ごはんを食べませんでした。", reading: "あさごはんをたべませんでした。", english: "I didn't eat breakfast." },
        ],
    },
    {
        id: "n5-te-kudasai",
        pattern: "〜てください",
        jlptLevel: 5,
        title: "てください - please do ~",
        meaning: "A polite request asking someone to do something.",
        explanation:
            "Attaching ください to a verb's て-form makes a polite request or instruction - \"please do X.\" " +
            "It's one of the first practical uses of the て-form a beginner learns, since the て-form itself is " +
            "the connective base for several other grammar patterns too.",
        formation: "Verb て-form + ください",
        examples: [
            { japanese: "ここに名前を書いてください。", reading: "ここになまえをかいてください。", english: "Please write your name here." },
            { japanese: "少し待ってください。", reading: "すこしまってください。", english: "Please wait a moment." },
        ],
    },
    {
        id: "n5-teimasu-continuous",
        pattern: "〜ています",
        jlptLevel: 5,
        title: "ています - ongoing action or resulting state",
        meaning: "Describes an action in progress, a repeated habit, or the lasting result of a past action.",
        explanation:
            "The て-form plus います most often describes an action happening right now, similar to English " +
            "\"-ing.\" It also covers repeated habits (\"I work at a bank\") and, for certain verbs, a state that " +
            "resulted from something happening once, like 結婚しています (\"[I] am married,\" literally \"have " +
            "gotten married and that still holds\").",
        formation: "Verb て-form + います",
        examples: [
            { japanese: "今、テレビを見ています。", reading: "いま、テレビをみています。", english: "I'm watching TV right now." },
            { japanese: "田中さんは銀行で働いています。", reading: "たなかさんはぎんこうではたらいています。", english: "Mr. Tanaka works at a bank." },
        ],
    },
    {
        id: "n5-tai-desu",
        pattern: "〜たいです",
        jlptLevel: 5,
        title: "たいです - want to do ~",
        meaning: "Expresses the speaker's own desire to do something.",
        explanation:
            "Replacing a verb's ます with たいです expresses that the speaker wants to do that action. It's " +
            "normally only used for the speaker's own wishes (or, in a question, the listener's) - describing " +
            "what a third party wants needs a different, more indirect pattern.",
        formation: "Verb stem + たいです",
        examples: [
            { japanese: "日本へ行きたいです。", reading: "にほんへいきたいです。", english: "I want to go to Japan." },
            { japanese: "何が食べたいですか。", reading: "なにがたべたいですか。", english: "What do you want to eat?" },
        ],
    },
    {
        id: "n5-koto-ga-dekimasu",
        pattern: "〜ことができます",
        jlptLevel: 5,
        title: "ことができます - can do ~ (ability/possibility)",
        meaning: "States that an action is possible or that the speaker has the ability to do it.",
        explanation:
            "Turning a verb into a noun-like phrase with こと and following it with できます (\"is possible/able\") " +
            "expresses general ability or possibility. It's a safe, beginner-friendly way to say \"can\" before " +
            "learning the shorter potential verb forms taught at a later level.",
        formation: "Verb (dictionary form) + ことができます",
        examples: [
            {
                japanese: "田中さんは日本語を話すことができます。",
                reading: "たなかさんはにほんごをはなすことができます。",
                english: "Mr. Tanaka can speak Japanese.",
            },
            {
                japanese: "ここで写真を撮ることができません。",
                reading: "ここでしゃしんをとることができません。",
                english: "You cannot take photos here.",
            },
        ],
    },
    {
        id: "n5-mashou",
        pattern: "〜ましょう",
        jlptLevel: 5,
        title: "ましょう - let's do ~",
        meaning: "A friendly suggestion to do something together.",
        explanation:
            "Swapping ます for ましょう turns a verb into a suggestion - \"let's do X\" - proposing an action to " +
            "do along with the listener. It's warmer and more direct than ませんか (below), which asks rather " +
            "than proposes.",
        formation: "Verb stem + ましょう",
        examples: [
            { japanese: "一緒に昼ご飯を食べましょう。", reading: "いっしょにひるごはんをたべましょう。", english: "Let's eat lunch together." },
            { japanese: "そろそろ帰りましょう。", reading: "そろそろかえりましょう。", english: "Let's head home soon." },
        ],
    },
    {
        id: "n5-masenka",
        pattern: "〜ませんか",
        jlptLevel: 5,
        title: "ませんか - won't you do ~? (invitation)",
        meaning: "A polite invitation, literally asking \"won't you do X?\" but functioning as \"would you like to?\"",
        explanation:
            "Adding か to ません produces a soft invitation. Phrasing it as a negative question makes it gentler " +
            "and less pushy than a direct request, leaving the listener an easy, natural way to decline if they want to.",
        formation: "Verb stem + ませんか",
        examples: [
            {
                japanese: "今晩、一緒に食事をしませんか。",
                reading: "こんばん、いっしょにしょくじをしませんか。",
                english: "Would you like to have dinner together tonight?",
            },
            { japanese: "映画を見に行きませんか。", reading: "えいがをみにいきませんか。", english: "Would you like to go see a movie?" },
        ],
    },
    {
        id: "n5-i-adjective-desu",
        pattern: "Xは 〜いです",
        jlptLevel: 5,
        title: "い-adjectives + です",
        meaning: "Describes X using an い-adjective, in the polite present tense.",
        explanation:
            "い-adjectives (ending in い, like 高い or 大きい) already carry their own present-tense ending, so " +
            "です here just adds politeness - it doesn't act as a copula the way it does after a noun. To " +
            "negate, drop the final い and add くないです, and for the past, くなかったです (or the slightly more " +
            "formal くありませんでした).",
        formation: "Noun + は + い-adjective + です",
        examples: [
            { japanese: "この本は面白いです。", reading: "このほんはおもしろいです。", english: "This book is interesting." },
            { japanese: "今日は暑くないです。", reading: "きょうはあつくないです。", english: "Today isn't hot." },
        ],
    },
    {
        id: "n5-na-adjective-desu",
        pattern: "Xは 〜です / 〜な + Noun",
        jlptLevel: 5,
        title: "な-adjectives + です",
        meaning: "Describes X using a な-adjective, in the polite present tense.",
        explanation:
            "な-adjectives (like 静か or 便利) behave more like nouns grammatically: です attaches directly and " +
            "does act as a genuine copula here. The な only appears when the adjective directly modifies a " +
            "following noun (静かな部屋, \"a quiet room\") - drop it when the adjective ends the sentence before です.",
        formation: "Noun + は + な-adjective + です / な-adjective + な + Noun",
        examples: [
            { japanese: "この公園は静かです。", reading: "このこうえんはしずかです。", english: "This park is quiet." },
            { japanese: "便利なアプリですね。", reading: "べんりなアプリですね。", english: "That's a convenient app, isn't it." },
        ],
    },
    {
        id: "n5-kute-de-and",
        pattern: "い-adjective (〜くて) / な-adjective・Noun (〜で) + ...",
        jlptLevel: 5,
        title: "くて / で - joining adjectives and nouns with \"and\"",
        meaning: "Links two descriptive clauses together - the adjective/noun equivalent of the verb て-form.",
        explanation:
            "Verbs have their own て-form for chaining clauses together (\"do X, and then Y\"), and adjectives " +
            "and nouns need an equivalent of their own: い-adjectives drop the final い and add くて, while " +
            "な-adjectives and nouns both simply add で. Either can also carry a loose sense of \"because,\" " +
            "similar to an unmarked から.",
        formation: "い-adjective (drop い) + くて / な-adjective・Noun + で",
        examples: [
            { japanese: "この部屋は広くて明るいです。", reading: "このへやはひろくてあかるいです。", english: "This room is spacious and bright." },
            { japanese: "彼は学生で、毎日忙しいです。", reading: "かれはがくせいで、まいにちいそがしいです。", english: "He's a student, and busy every day." },
        ],
    },
    {
        id: "n5-ni-iku-purpose",
        pattern: "Verb (ます-stem) + に行く / に来る / に帰る",
        jlptLevel: 5,
        title: "〜に行く - going (somewhere) to do ~",
        meaning: "States the purpose of a trip: going, coming, or returning in order to do something.",
        explanation:
            "The verb's ます-stem (drop ます) acts as a noun-like form here, which is why に can attach to it - " +
            "plain verbs and the て-form aren't nouns, only the stem is. 行く is by far the most common partner, " +
            "but 来る (\"come to do X\") and 帰る (\"go home to do X\") work exactly the same way.",
        formation: "Verb (ます-stem) + に + 行く / 来る / 帰る",
        examples: [
            {
                japanese: "デパートへ靴を買いに行きます。",
                reading: "デパートへくつをかいにいきます。",
                english: "I'm going to the department store to buy shoes.",
            },
            {
                japanese: "友達と映画を見に行きました。",
                reading: "ともだちとえいがをみにいきました。",
                english: "I went to see a movie with a friend.",
            },
        ],
    },
    {
        id: "n5-kara-reason",
        pattern: "〜から、〜",
        jlptLevel: 5,
        title: "から - because, since (giving a reason)",
        meaning: "Marks the reason for what follows; \"because X, Y.\"",
        explanation:
            "Attached directly to the end of a plain or polite sentence, から gives the reason for the clause " +
            "that follows - the reason always comes first, then から, then the result. Unlike verb and " +
            "adjective endings, から doesn't change anything about the sentence it attaches to; it just tacks " +
            "on the end. Don't confuse this から with the から that means \"from\" after a place or time " +
            "(日本から, \"from Japan\") - that one attaches to a single noun, this one attaches to a whole sentence.",
        formation: "Sentence (plain or polite form) + から、+ Result",
        examples: [
            {
                japanese: "時間がないから、急いでください。",
                reading: "じかんがないから、いそいでください。",
                english: "Since there's no time, please hurry.",
            },
            {
                japanese: "疲れましたから、少し休みます。",
                reading: "つかれましたから、すこしやすみます。",
                english: "Since I'm tired, I'll rest a little.",
            },
        ],
    },
    {
        id: "n4-tari-tari",
        pattern: "〜たり〜たりします",
        jlptLevel: 4,
        title: "たり〜たりします - doing things like ~ and ~",
        meaning: "Lists a couple of representative actions out of a larger, unstated set.",
        explanation:
            "Attaching たり to a verb's past-tense (た-form) stem and repeating the pattern for a second verb " +
            "lists a few example actions without claiming they're the only things done, or the order they " +
            "happened in - similar in spirit to や for nouns. します at the end carries the actual tense.",
        formation: "Verb (た-form) + り + Verb (た-form) + り + します",
        examples: [
            {
                japanese: "週末は本を読んだり、映画を見たりします。",
                reading: "しゅうまつはほんをよんだり、えいがをみたりします。",
                english: "On weekends I do things like read books and watch movies.",
            },
            {
                japanese: "公園で走ったり、歌ったりしました。",
                reading: "こうえんではしったり、うたったりしました。",
                english: "At the park we did things like run and sing.",
            },
        ],
    },
    {
        id: "n4-nagara",
        pattern: "〜ながら",
        jlptLevel: 4,
        title: "ながら - while doing ~",
        meaning: "Says the same person does two things at the same time, with one being the main action.",
        explanation:
            "Attaching ながら to a verb's stem means \"while doing that verb.\" Both actions must share the same " +
            "subject, and the verb that ながら attaches to is treated as the background action, while the main " +
            "verb of the sentence is what the speaker treats as the primary focus.",
        formation: "Verb stem + ながら",
        examples: [
            {
                japanese: "音楽を聞きながら勉強します。",
                reading: "おんがくをききながらべんきょうします。",
                english: "I study while listening to music.",
            },
            {
                japanese: "テレビを見ながら朝ごはんを食べました。",
                reading: "テレビをみながらあさごはんをたべました。",
                english: "I ate breakfast while watching TV.",
            },
        ],
    },
    {
        id: "n4-sou-desu-appearance",
        pattern: "〜そうです",
        jlptLevel: 4,
        title: "そうです - looks like ~ (appearance)",
        meaning: "Says something appears to be the case, based on what the speaker can see.",
        explanation:
            "Attached to an い-adjective's stem (dropping the final い) or a verb's stem, そうです expresses a " +
            "judgment based on visual appearance - \"it looks like it's going to rain\" rather than an actual " +
            "confirmed fact. Note いい becomes よさそうです, an irregular exception worth memorizing on its own.",
        formation: "い-adjective stem / Verb stem + そうです",
        examples: [
            { japanese: "このケーキはおいしそうです。", reading: "このケーキはおいしそうです。", english: "This cake looks delicious." },
            { japanese: "雨が降りそうです。", reading: "あめがふりそうです。", english: "It looks like it's going to rain." },
        ],
    },
    {
        id: "n4-deshou",
        pattern: "〜でしょう",
        jlptLevel: 4,
        title: "でしょう - probably ~",
        meaning: "Softens a statement into a probable guess rather than a certain fact.",
        explanation:
            "でしょう is a less certain version of です, used to guess or predict something rather than state it " +
            "outright. Spoken with rising intonation, it also seeks agreement, closer to English \"right?\"",
        formation: "Noun/Adjective/Verb (plain form) + でしょう",
        examples: [
            { japanese: "明日は晴れるでしょう。", reading: "あしたははれるでしょう。", english: "It will probably be sunny tomorrow." },
            { japanese: "田中さんはもう帰ったでしょう。", reading: "たなかさんはもうかえったでしょう。", english: "Mr. Tanaka has probably already gone home." },
        ],
    },
    {
        id: "n4-to-omoimasu",
        pattern: "〜と思います",
        jlptLevel: 4,
        title: "と思います - I think that ~",
        meaning: "Introduces the speaker's own opinion or belief.",
        explanation:
            "Placing と思います after a plain-form sentence marks it as the speaker's opinion, rather than a " +
            "flat statement of fact. It's a common, softening way to voice a view without sounding overly direct.",
        formation: "Sentence (plain form) + と思います",
        examples: [
            {
                japanese: "この映画は面白いと思います。",
                reading: "このえいがはおもしろいとおもいます。",
                english: "I think this movie is interesting.",
            },
            {
                japanese: "明日は雨が降ると思います。",
                reading: "あしたはあめがふるとおもいます。",
                english: "I think it will rain tomorrow.",
            },
        ],
    },
    {
        id: "n4-mae-ni",
        pattern: "〜前に",
        jlptLevel: 4,
        title: "前に - before doing ~",
        meaning: "Marks an action that happens before another one.",
        explanation:
            "Attached after a verb's dictionary form (never the past tense, even when the whole sentence is " +
            "past), 前に says that action hasn't happened yet at the point being described - \"before doing X.\" " +
            "With a noun instead of a verb, の前に works the same way for time or place (\"before/in front of\").",
        formation: "Verb (dictionary form) + 前に / Noun + の前に",
        examples: [
            { japanese: "寝る前に歯を磨きます。", reading: "ねるまえにはをみがきます。", english: "I brush my teeth before going to sleep." },
            { japanese: "食事の前に手を洗ってください。", reading: "しょくじのまえにてをあらってください。", english: "Please wash your hands before the meal." },
        ],
    },
    {
        id: "n4-ato-de",
        pattern: "〜たあとで",
        jlptLevel: 4,
        title: "たあとで - after doing ~",
        meaning: "Marks an action that happens after another one has finished.",
        explanation:
            "Attached to a verb's た-form, あとで says the main action happens after the first one is complete. " +
            "With a noun instead of a verb, の後で works the same way (\"after [noun]\").",
        formation: "Verb (た-form) + あとで / Noun + の後で",
        examples: [
            { japanese: "宿題をしたあとで遊びます。", reading: "しゅくだいをしたあとであそびます。", english: "I play after doing my homework." },
            { japanese: "仕事の後で買い物に行きました。", reading: "しごとのあとでかいものにいきました。", english: "I went shopping after work." },
        ],
    },
    {
        id: "n4-koto-ga-arimasu",
        pattern: "〜たことがあります",
        jlptLevel: 4,
        title: "たことがあります - have done ~ before",
        meaning: "States that the speaker has experienced doing something at least once.",
        explanation:
            "Attached to a verb's た-form, ことがあります describes a past experience - \"I have done X before\" " +
            "- separate from simply saying something happened, since it emphasizes the experience itself rather " +
            "than when it happened.",
        formation: "Verb (た-form) + ことがあります",
        examples: [
            {
                japanese: "日本へ行ったことがあります。",
                reading: "にほんへいったことがあります。",
                english: "I have been to Japan before.",
            },
            {
                japanese: "納豆を食べたことがありません。",
                reading: "なっとうをたべたことがありません。",
                english: "I have never eaten natto.",
            },
        ],
    },
    {
        id: "n4-nakereba-narimasen",
        pattern: "〜なければなりません",
        jlptLevel: 4,
        title: "なければなりません - must do ~",
        meaning: "Expresses an obligation: this needs to be done.",
        explanation:
            "Built from a verb's negative ない-form (dropping the final い and adding ければ), plus なりません " +
            "(\"it won't do [otherwise]\"), this literally means \"if [X] isn't done, it won't do\" - in practice, " +
            "a strong \"must.\"",
        formation: "Verb (ない-form, drop い) + ければなりません",
        examples: [
            {
                japanese: "毎日薬を飲まなければなりません。",
                reading: "まいにちくすりをのまなければなりません。",
                english: "I have to take medicine every day.",
            },
            {
                japanese: "レポートを今日中に出さなければなりません。",
                reading: "レポートをきょうじゅうにださなければなりません。",
                english: "I have to hand in the report by the end of today.",
            },
        ],
    },
    {
        id: "n4-nakutemo-ii",
        pattern: "〜なくてもいいです",
        jlptLevel: 4,
        title: "なくてもいいです - don't have to do ~",
        meaning: "Says an action is not required - the opposite of an obligation.",
        explanation:
            "Built from a verb's negative て-form (なくて) plus もいいです (\"even if... it's fine\"), this says " +
            "there's no need to do the action - it's permitted to skip it, unlike なければなりません above, which " +
            "insists it be done.",
        formation: "Verb (ない-form, drop い) + くてもいいです",
        examples: [
            {
                japanese: "明日は仕事に行かなくてもいいです。",
                reading: "あしたはしごとにいかなくてもいいです。",
                english: "I don't have to go to work tomorrow.",
            },
            {
                japanese: "全部食べなくてもいいですよ。",
                reading: "ぜんぶたべなくてもいいですよ。",
                english: "You don't have to eat all of it.",
            },
        ],
    },
    {
        id: "n4-temo-ii",
        pattern: "〜てもいいです",
        jlptLevel: 4,
        title: "てもいいです - may do ~ (permission)",
        meaning: "Grants or asks for permission to do something.",
        explanation:
            "Attaching もいいです to a verb's て-form asks or grants permission - \"even if you do X, it's fine\" " +
            "in a literal sense, \"you may do X\" in practice. Raising the pitch turns the statement into a " +
            "request for permission instead.",
        formation: "Verb て-form + もいいです",
        examples: [
            { japanese: "ここに座ってもいいですか。", reading: "ここにすわってもいいですか。", english: "May I sit here?" },
            { japanese: "写真を撮ってもいいです。", reading: "しゃしんをとってもいいです。", english: "You may take photos." },
        ],
    },
    {
        id: "n4-tewa-ikemasen",
        pattern: "〜てはいけません",
        jlptLevel: 4,
        title: "てはいけません - must not do ~",
        meaning: "Prohibits an action - the stronger opposite of てもいいです.",
        explanation:
            "Attaching はいけません to a verb's て-form (usually contracted to ちゃいけません in casual speech) " +
            "forbids the action, more firmly than a simple negative request.",
        formation: "Verb て-form + はいけません",
        examples: [
            { japanese: "ここでたばこを吸ってはいけません。", reading: "ここでたばこをすってはいけません。", english: "You must not smoke here." },
            { japanese: "授業中に寝てはいけません。", reading: "じゅぎょうちゅうにねてはいけません。", english: "You must not sleep during class." },
        ],
    },
    {
        id: "n4-tara-conditional",
        pattern: "〜たら",
        jlptLevel: 4,
        title: "たら - if/when ~ (conditional)",
        meaning: "The most flexible everyday conditional: if/when the first part happens, the second follows.",
        explanation:
            "Built from a verb, adjective, or noun's past-tense (た-form) plus ら, this is the broadest, most " +
            "casual-friendly conditional in Japanese, usable for one-off hypotheticals, sequences of events, and " +
            "everyday advice alike, unlike the more restricted と and narrower ば forms.",
        formation: "Verb/Adjective/Noun (た-form) + ら",
        examples: [
            { japanese: "雨が降ったら、家にいます。", reading: "あめがふったら、いえにいます。", english: "If it rains, I'll stay home." },
            { japanese: "駅に着いたら、電話してください。", reading: "えきについたら、でんわしてください。", english: "Please call me once you get to the station." },
        ],
    },
    {
        id: "n4-ba-conditional",
        pattern: "〜ば",
        jlptLevel: 4,
        title: "ば - if ~ (conditional)",
        meaning: "A more formal conditional, often used for general truths and advice.",
        explanation:
            "Formed by changing a verb's final u-sound to its e-sound equivalent and adding ば (for い-adjectives, " +
            "drop い and add ければ), this conditional focuses purely on the logical relationship between the two " +
            "clauses - it reads a little more formal or written than たら, and is common in set expressions and general advice.",
        formation: "Verb (え-row + ば) / い-adjective (drop い + ければ)",
        examples: [
            { japanese: "急げば、間に合います。", reading: "いそげば、まにあいます。", english: "If you hurry, you'll make it in time." },
            { japanese: "安ければ買います。", reading: "やすければかいます。", english: "If it's cheap, I'll buy it." },
        ],
    },
    {
        id: "n4-node",
        pattern: "〜ので",
        jlptLevel: 4,
        title: "ので - because ~ (softer reason)",
        meaning: "Gives a reason, more polite and objective-sounding than から.",
        explanation:
            "ので attaches after a plain-form clause (な-adjectives and nouns take なので) to give a reason. It " +
            "reads as a softer, more matter-of-fact justification than から, so it's common when explaining a " +
            "reason politely, like asking for a favor or excusing yourself.",
        formation: "Sentence (plain form) + ので",
        examples: [
            {
                japanese: "今日は忙しいので、行けません。",
                reading: "きょうはいそがしいので、いけません。",
                english: "Since I'm busy today, I can't go.",
            },
            {
                japanese: "頭が痛いので、早く帰ります。",
                reading: "あたまがいたいので、はやくかえります。",
                english: "Since I have a headache, I'll go home early.",
            },
        ],
    },
    {
        id: "n4-noni",
        pattern: "〜のに",
        jlptLevel: 4,
        title: "のに - even though ~",
        meaning: "Introduces a result that contradicts what would normally be expected.",
        explanation:
            "のに connects two clauses where the second is surprising or disappointing given the first - " +
            "\"even though X, Y,\" often carrying a note of frustration or regret that から/ので don't have.",
        formation: "Sentence (plain form) + のに",
        examples: [
            {
                japanese: "薬を飲んだのに、まだ熱があります。",
                reading: "くすりをのんだのに、まだねつがあります。",
                english: "Even though I took medicine, I still have a fever.",
            },
            {
                japanese: "一生懸命勉強したのに、テストに落ちました。",
                reading: "いっしょうけんめいべんきょうしたのに、テストにおちました。",
                english: "Even though I studied hard, I failed the test.",
            },
        ],
    },
    {
        id: "n4-tsumori",
        pattern: "〜つもりです",
        jlptLevel: 4,
        title: "つもりです - intend to do ~",
        meaning: "States a plan or intention, usually one already firmly decided.",
        explanation:
            "Attached to a verb's dictionary form (or ない-form for a negative intention), つもりです expresses a " +
            "settled personal intention - stronger and more decided than a vague hope, though not yet a fixed " +
            "arrangement like a booked reservation.",
        formation: "Verb (dictionary form / ない-form) + つもりです",
        examples: [
            {
                japanese: "来年、日本へ留学するつもりです。",
                reading: "らいねん、にほんへりゅうがくするつもりです。",
                english: "I intend to study abroad in Japan next year.",
            },
            {
                japanese: "今日はお酒を飲まないつもりです。",
                reading: "きょうはおさけをのまないつもりです。",
                english: "I don't intend to drink alcohol today.",
            },
        ],
    },
    {
        id: "n4-yasui-nikui",
        pattern: "〜やすい / 〜にくい",
        jlptLevel: 4,
        title: "やすい / にくい - easy / hard to do ~",
        meaning: "Describes how easy or difficult an action is to perform, as a quality of the verb itself.",
        explanation:
            "Attached to a verb's stem, やすい (\"easy to\") and にくい (\"hard to\") turn the verb into an " +
            "い-adjective describing how easy or difficult that action is in general - not about a specific " +
            "person's skill, but a property of the thing being acted on, like a pen that's easy to write with.",
        formation: "Verb stem + やすい / にくい",
        examples: [
            { japanese: "このペンは書きやすいです。", reading: "このペンはかきやすいです。", english: "This pen is easy to write with." },
            { japanese: "この漢字は読みにくいです。", reading: "このかんじはよみにくいです。", english: "This kanji is hard to read." },
        ],
    },
];
